import { useMemo, useState } from 'react';
import { Center, Checkbox, Flex, Grid, Input } from '@chakra-ui/react';
import { EvaluationEvent } from '@keyops-cep/api-client';

import i18n from '../../utils/i18n';

import { ListEventCard } from './components/ListEventCard';
import { QRCodeModal } from './components/QRCodeModal';

type EvaluationEventsListProperties = {
  evaluationEvents: EvaluationEvent[];
};

export const EvaluationEventsList = ({
  evaluationEvents,
}: EvaluationEventsListProperties) => {
  const [selectedEvent, setSelectedEvent] = useState<EvaluationEvent | null>(
    null
  );
  const [search, setSearch] = useState('');
  const [showOlder, setShowOlder] = useState(false);

  // Filter events based on end date + 3 days < now, unless showOlder is checked
  const filteredEvents = useMemo(() => {
    const now = new Date();
    return evaluationEvents
      .filter((event) => {
        if (showOlder) return true;
        const endDate = new Date(event.endDate);
        const threeDaysAfterEnd = new Date(
          endDate.getTime() + 3 * 24 * 60 * 60 * 1000
        );
        return threeDaysAfterEnd >= now;
      })
      .sort(
        (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
      );
  }, [evaluationEvents, showOlder]);

  // Search logic
  const searchedEvents = useMemo(() => {
    if (!search) return filteredEvents;
    const searchLower = search.toLowerCase();
    return filteredEvents.filter((event) => {
      const topicNames = (event.topics ?? []).map((t) => t.name).join(' ');
      const topicCodes = (event.topics ?? []).map((t) => t.code).join(' ');
      const speakerNames = (event.speakers ?? []).map((s) => s.name).join(' ');
      const moderatorNames = (event.moderators ?? [])
        .map((m) => m.name)
        .join(' ');
      return [
        event.name,
        topicNames,
        topicCodes,
        event.learningObjectives,
        speakerNames,
        moderatorNames,
        event.department,
        event.therapeuticArea,
        event.locationName,
        event.externalId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(searchLower);
    });
  }, [filteredEvents, search]);

  const handleQrOpen = (event: EvaluationEvent) => {
    setSelectedEvent(event);
  };

  return (
    <>
      <Flex mb={4} align="center" gap={4} flexWrap="wrap">
        <Input
          placeholder={i18n.t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxW="400px"
        />
        <Checkbox
          isChecked={showOlder}
          onChange={(e) => setShowOlder(e.target.checked)}
          data-testid="show-older-checkbox"
        >
          {i18n.t('evaluationEvent.showOlderEvents')}
        </Checkbox>
      </Flex>
      {searchedEvents.length === 0 ? (
        <Center>{i18n.t('common.noData')}</Center>
      ) : (
        <Grid
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
          gap={6}
        >
          {searchedEvents.map((event) => (
            <ListEventCard
              key={event.id}
              event={event}
              onOpenQr={handleQrOpen}
            />
          ))}
        </Grid>
      )}
      {selectedEvent && (
        <QRCodeModal
          isOpen={!!selectedEvent}
          onClose={() => setSelectedEvent(null)}
          event={selectedEvent}
        />
      )}
    </>
  );
};
